import { useState } from "react";
import { Modal, Select, Input } from "antd";
import { useNavigate } from "react-router-dom";
import { useRecordSend } from "../hooks/useProposalSends";
import { buildDocumentHtml } from "./documentHtml";
import { withCountryCode } from "../utils/phones";
import { tenDigits } from "../hooks/useCustomerLookup";
import ExistingCustomerNotice from "./ExistingCustomerNotice";

/**
 * Send the document, and log that it went.
 *
 * On the email channel the document goes as a complete, self-contained HTML
 * page — markup and stylesheet together — lifted from the very preview the rep
 * was looking at, so the client cannot receive a second, different rendering.
 * Every other channel only records the send, because printing or copying leaves
 * no trace of its own.
 */
const CHANNELS = [
  { value: "email", label: "Email — send it now" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "download", label: "Download PDF" },
];

/**
 * Hand the rep the PDF the server just made.
 *
 * The download channel does not mail anything — it logs the send and returns the
 * rendered file, which the rep then delivers themselves.
 */
const savePdf = (base64, name) => {
  const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name || "document.pdf";
  a.click();
  URL.revokeObjectURL(url);
};

/**
 * The WhatsApp hand-off.
 *
 * Nothing sends the message for us — there is no WhatsApp Business API here — so
 * this opens the chat with the number and the text already filled in. wa.me
 * cannot carry a file, which is why the PDF is downloaded first for the rep to
 * attach by hand.
 */
const waLink = (phone, { name, brand, doc }) => {
  const digits = String(phone || "").replace(/\D/g, "");
  const text = encodeURIComponent(
    `Hi ${name || "there"}, please find your ${brand || "Entermark"} ${doc} attached. `
    + "Reply here for anything.",
  );
  return `https://wa.me/${digits}?text=${text}`;
};

/** A non-negative whole number, or null when the field was left empty. */
const wholeDays = (v) => {
  if (v === "" || v == null) return null;
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n >= 0 ? n : null;
};

const SendModal = ({
  doc, form, quote, validUntil, validityDays, brandName, docNode, quotationNode,
  existingCustomer, attorneyTrademarks, initialChannel = "email", onClose,
}) => {
  const navigate = useNavigate();
  const record = useRecordSend();
  const [email, setEmail] = useState(form.email || "");
  const [channel, setChannel] = useState(initialChannel);
  const [subject, setSubject] = useState(
    `Your ${doc === "quotation" ? "quotation" : "proposal"}${form.client_name ? ` — ${form.client_name}` : ""}`,
  );
  const [note, setNote] = useState("");
  const [phone, setPhone] = useState(withCountryCode(form.country_code, form.phone));
  /* Set only when the browser refused to open WhatsApp for us — the send is
     already logged by then, so the rep needs the link rather than a dead end. */
  const [blockedLink, setBlockedLink] = useState("");
  const [error, setError] = useState("");

  const isEmail = channel === "email";
  const isDownload = channel === "download";
  // WhatsApp goes to a number, not an address — asking for the wrong one is how
  // a send gets logged against nothing.
  const isWhatsapp = channel === "whatsapp";
  const busy = record.isPending;

  const submit = () => {
    if (isEmail && !email.trim()) return setError("An email channel needs an address to send to.");
    if (isWhatsapp && !phone.trim()) return setError("A WhatsApp send needs a number.");
    setError("");

    let html;
    try {
      html = buildDocumentHtml(docNode, subject);
    } catch (err) {
      return setError(err?.message || "The document could not be prepared");
    }

    /* Sent with the proposal, not after it: on acceptance the server has to
       produce a quotation immediately, and this is the one the rep reviewed.
       Without it the server falls back to a plain one nobody has seen. */
    let quotationHtml = null;
    if (quotationNode) {
      try {
        quotationHtml = buildDocumentHtml(quotationNode, `Quotation — ${form.client_name || ""}`);
      } catch {
        // A missing quotation is worth sending the proposal without.
      }
    }

    record.mutate(
      {
        doc_type: doc,
        channel,
        sent_to_email: email.trim() || null,
        /* The number travels as two fields: a dial code and ten digits. Joined
           into one string it has to be taken apart again at every stop, and a
           re-joined value fed back through a form is how it grew a second code.
           `sent_to_phone` stays only because the WhatsApp validation reads it. */
        country_code: form.country_code || "+91",
        mobile: tenDigits(phone) || null,
        sent_to_phone: phone.trim() || null,
        recipient_name: form.contact_name || null,
        client_name: form.client_name || null,
        lead_id: form.lead_id ?? null,
        /* The lookup already found them; without this the payment would open a
           second account instead of renewing the one the banner warned about. */
        customer_id: form.customer_id ?? existingCustomer?.id ?? null,
        segment: quote?.segment || form.segment,
        plan_name: quote?.plan || null,
        marks: quote?.marks ?? (Number(form.marks) || null),
        frequency: quote?.frequency || form.frequency,
        /* The inputs the server re-prices from on acceptance. Sent alongside the
           snapshot rather than only inside it, because the server reads these
           without unpacking the payload. */
        users: Number(form.users) || 1,
        clients: Number(form.clients) || 0,
        package: form.package,
        additional_discount: Number(form.additional_discount) || 0,
        onboarding_discount: Number(form.onboarding_discount) || 0,
        // The headline figure: what the client actually pays first.
        amount: quote?.pricing?.first_invoice ?? quote?.pricing?.term_invoice ?? null,
        valid_until: validUntil,
        // Everything needed to rebuild this document from the log later.
        payload: {
          ...form,
          email: email.trim() || form.email || null,
          /* Digits and code stay separate here — writing the combined string
              back over form.phone is what made the next prefill add a second
              code, and the one after that a third. `sent_to_phone` above is the
              formatted one, written once. */
          phone: tenDigits(form.phone) || null,
          // Same ten digits under the name the activation step reads.
          mobile: tenDigits(form.phone) || null,
          country_code: form.country_code || "+91",
          recipient_name: form.contact_name || null,
          client_name: form.client_name || null,
          validity_days: validityDays,
          /* Whole days or nothing: a blank box means "use the default", which is
             the server's to decide, not an empty string to be parsed as zero. */
          grace_period_days: wholeDays(form.grace_period_days),
          reminder_before_days: wholeDays(form.reminder_before_days),
          /* Per-number counts as looked up for this quote. `marks` above is the
             deduped total, so these do not add up to it when one trademark sits
             under two numbers — they are the breakdown, not the sum. */
          ...(attorneyTrademarks?.length ? { attorney_trademarks: attorneyTrademarks } : {}),
          ...(quotationHtml ? { quotation_html: quotationHtml } : {}),
        },
        subject,
        email_body: note.trim() || null,
        // A whole page, stylesheet included — not the bare fragment.
        html,
      },
      {
        onSuccess: (res) => {
          const { pdf_base64, pdf_name } = res?.data || {};
          if (pdf_base64) savePdf(pdf_base64, pdf_name);

          if (isWhatsapp) {
            const link = waLink(phone, { name: form.contact_name, brand: brandName, doc });
            /* A popup opened after a round trip is often blocked. Losing it here
               would strand a send that is already in the log. */
            if (!window.open(link, "_blank", "noopener")) {
              setBlockedLink(link);
              return;
            }
          }
          onClose();
          navigate("/cms/proposals");
        },
      },
    );
  };

  const label = { fontSize: 12, fontWeight: 700, color: "#374151", display: "block", marginBottom: 6 };

  return (
    <Modal
      open
      title={<span style={{ fontWeight: 700 }}>{isDownload ? "Download" : "Send"} this {doc}</span>}
      onCancel={onClose}
      onOk={submit}
      okText={
        record.isPending
          ? (isEmail ? "Sending…" : "Preparing PDF…")
          : isEmail ? "Send email"
            : isWhatsapp ? "Get PDF & open WhatsApp"
              : isDownload ? "Download PDF" : "Record send"
      }
      okButtonProps={{ loading: busy }}
      cancelButtonProps={{ disabled: busy }}
      footer={blockedLink ? null : undefined}
      destroyOnClose
      width={460}
    >
      {blockedLink ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 4 }}>
          <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.6 }}>
            Recorded, and the PDF has downloaded. Your browser blocked the WhatsApp
            window — open it here and attach the file.
          </div>
          <a
            href={blockedLink} target="_blank" rel="noreferrer"
            onClick={() => { onClose(); navigate("/cms/proposals"); }}
            style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "10px 18px", borderRadius: 9, background: "#25D366", color: "#fff", fontWeight: 700, fontSize: 13.5, textDecoration: "none" }}
          >
            Open WhatsApp
          </a>
          <button
            onClick={() => { onClose(); navigate("/cms/proposals"); }}
            style={{ padding: "8px 18px", borderRadius: 9, border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", fontWeight: 700, fontSize: 13, cursor: "pointer" }}
          >
            Skip — I'll send it myself
          </button>
        </div>
      ) : (
      <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 4 }}>
        <div>
          <label style={label}>Channel</label>
          <Select value={channel} onChange={setChannel} style={{ width: "100%" }} options={CHANNELS} />
        </div>
        {(isEmail || isWhatsapp) && (
        <div>
          <label style={label}>
            {isWhatsapp ? "Send to (number)" : "Send to"}{" "}
            <span style={{ color: "#dc2626" }}>*</span>
          </label>
          {isWhatsapp ? (
            <Input
              value={phone}
              onChange={(e) => { setPhone(e.target.value); setError(""); }}
              placeholder="+91 98xxx xxxxx"
            />
          ) : (
            <Input
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              placeholder="client@example.com"
            />
          )}
        </div>
        )}
        {isEmail && (
          <>
            <div>
              <label style={label}>Subject</label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
            </div>
            <div>
              <label style={label}>Cover note <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span></label>
              <Input.TextArea
                rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Leave blank to use the standard note."
              />
            </div>
          </>
        )}
        {/* Last gate before it goes out. */}
        <ExistingCustomerNotice customer={existingCustomer} compact />

        <div style={{ fontSize: 11.5, color: "#9ca3af", lineHeight: 1.6 }}>
          {isEmail
            ? "The document on the right is sent exactly as it appears."
            : isWhatsapp
              ? "The PDF downloads and WhatsApp opens on this number with a message ready. Attach the PDF there — WhatsApp links cannot carry a file."
              : isDownload
                ? "Nothing is emailed. The PDF is rendered and downloaded for you to send yourself."
                : "Nothing is emailed — this only records that the document went out."}{" "}
          It lands in the log as <b>sent</b>; mark it viewed, accepted or rejected from
          the list once you hear back.
        </div>
        {(error || record.error) && (
          <div style={{ color: "#dc2626", fontSize: 12.5 }}>{error || record.error.message}</div>
        )}
      </div>
      )}
    </Modal>
  );
};

export default SendModal;
